import React from 'react';
import { Navigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import AdminShiftDetail from '../pages/admin/AdminShiftDetail';

/**
 * Role-aware router for /admin/shifts/:shiftId.
 *
 * SMS / email notifications historically deep-link to /admin/shifts/{id}.
 * Talent tapping that link must land DIRECTLY on the gig they can apply to
 * — never an admin-only page and never a "search for it" dead-end. This
 * wrapper inspects the signed-in role and routes accordingly:
 *   • admin / super_admin → the admin shift-detail page (unchanged)
 *   • client              → their client shift page
 *   • talent / other      → the talent gig-detail / apply page
 *   • logged-out          → login, preserving ?redirect= so they bounce
 *                           straight to the gig after signing in
 * Any query string (e.g. ?replace=1, ?src=...) is preserved.
 */
export default function ShiftLinkRouter() {
  const { user, loading } = useAuth();
  const { shiftId } = useParams();
  const { search } = useLocation();

  if (loading) return null;

  const gigPath = `/talent/gigs/${shiftId}${search || ''}`;

  if (!user) {
    return <Navigate to={`/login?redirect=${encodeURIComponent(gigPath)}`} replace />;
  }

  const role = user.role;
  if (role === 'admin' || role === 'super_admin') {
    return <AdminShiftDetail />;
  }
  if (role === 'client') {
    return <Navigate to={`/client/shifts/${shiftId}${search || ''}`} replace />;
  }
  // talent (and any other authenticated role) → apply/gig-detail page
  return <Navigate to={gigPath} replace />;
}
